import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminGetUsers, adminUpdateKYC, adminBlockUser, exportReport } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Shield, ShieldOff, Eye, CheckCircle, XCircle, Download, FileSpreadsheet, Users, UserCheck, UserX, Clock } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [kycFilter, setKycFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => { loadUsers(); }, [kycFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kycFilter) params.kyc_status = kycFilter;
      const res = await adminGetUsers(params);
      setUsers(res.data.users || []);
    } catch {} finally { setLoading(false); }
  };

  const handleKYC = async (userId, status) => {
    try {
      await adminUpdateKYC(userId, status);
      toast.success(`KYC status updated to ${status}`);
      loadUsers();
    } catch { toast.error("Failed to update"); }
  };

  const handleBlock = async (userId) => {
    try {
      const res = await adminBlockUser(userId);
      toast.success(`User ${res.data.new_status === 'blocked' ? 'blocked' : 'unblocked'}`);
      loadUsers();
    } catch { toast.error("Failed"); }
  };

  const filtered = users.filter(u => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.mobile || "").includes(q);
  });

  const stats = {
    total: users.length,
    verified: users.filter(u => u.kyc_status === 'verified').length,
    pending: users.filter(u => u.kyc_status === 'pending').length,
    rejected: users.filter(u => u.kyc_status === 'rejected').length,
  };

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
      verified: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
      completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
      rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-400' },
      active: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400' },
      blocked: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text} border ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{fontFamily:'Outfit, sans-serif'}}>User Management</h2>
          <p className="text-sm text-[#6B7280] mt-1">Manage customer accounts and KYC verification</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => exportReport('users', 'csv').then(r => { const b=new Blob([r.data],{type:'text/csv'}); const u=URL.createObjectURL(b); const a=document.createElement('a'); a.href=u; a.download='users.csv'; a.click(); toast.success('Users exported'); }).catch(() => toast.error('Export failed'))} data-testid="export-users-csv" className="bg-white border border-gray-200 text-[#0A2540] hover:bg-gray-50 hover:border-gray-300 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all shadow-sm hover:shadow">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
          <Button onClick={() => exportReport('users', 'pdf').then(r => { const b=new Blob([r.data],{type:'application/pdf'}); const u=URL.createObjectURL(b); const a=document.createElement('a'); a.href=u; a.download='users.pdf'; a.click(); toast.success('Users exported'); }).catch(() => toast.error('Export failed'))} data-testid="export-users-pdf" className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white hover:from-[#0D47A1] hover:to-[#0A3A8A] rounded-xl px-4 py-2.5 text-xs font-semibold transition-all shadow-sm hover:shadow-md">
            <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600', bg: 'bg-blue-50' },
          { label: 'KYC Verified', value: stats.verified, icon: UserCheck, color: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-50' },
          { label: 'KYC Pending', value: stats.pending, icon: Clock, color: 'from-amber-500 to-amber-600', bg: 'bg-amber-50' },
          { label: 'KYC Rejected', value: stats.rejected, icon: UserX, color: 'from-red-500 to-red-600', bg: 'bg-red-50' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all duration-300" style={{animationDelay: `${i * 100}ms`}}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">{stat.label}</p>
                <p className="text-3xl font-black text-[#0A2540] mt-1" style={{fontFamily:'Outfit, sans-serif'}}>{stat.value}</p>
              </div>
              <div className={`${stat.bg} p-3 rounded-xl`}>
                <stat.icon className={`h-6 w-6 bg-gradient-to-r ${stat.color} bg-clip-text`} style={{color: stat.color.includes('blue') ? '#3B82F6' : stat.color.includes('emerald') ? '#10B981' : stat.color.includes('amber') ? '#F59E0B' : '#EF4444'}} strokeWidth={1.8} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" strokeWidth={2} />
            <input data-testid="user-search" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by name or mobile..." className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none focus:bg-white transition-all" />
          </div>
          <select data-testid="kyc-filter" value={kycFilter} onChange={e => setKycFilter(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] focus:outline-none focus:bg-white transition-all cursor-pointer">
            <option value="">All KYC Status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{borderWidth: '3px'}} />
              <p className="text-sm text-[#6B7280]">Loading users...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-50/80 border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Customer</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">KYC Status</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">PAN</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Aadhaar</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Account</th>
                  <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, idx) => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition-colors duration-150 group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1565C0] to-[#00BCD4] flex items-center justify-center text-white font-bold text-sm shadow-sm">
                          {(u.name || 'U')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A2540]">{u.name || '—'}</p>
                          <p className="text-xs text-[#6B7280]">{u.mobile}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={u.kyc_status} /></td>
                    <td className="px-6 py-4">
                      {u.pan_verified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle className="h-4 w-4" /> <span className="text-xs font-medium">Verified</span></span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400"><XCircle className="h-4 w-4" /> <span className="text-xs">Not verified</span></span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {u.aadhaar_verified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle className="h-4 w-4" /> <span className="text-xs font-medium">Verified</span></span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400"><XCircle className="h-4 w-4" /> <span className="text-xs">Not verified</span></span>
                      )}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={u.status || 'active'} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => navigate(`/admin/user/${u.id}`)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 border border-blue-200 transition-colors">
                          <Eye className="h-3 w-3" /> View
                        </button>
                        {u.kyc_status === 'pending' && (
                          <>
                            <button onClick={() => handleKYC(u.id, 'verified')} data-testid={`verify-kyc-${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 border border-emerald-200 transition-colors">
                              <CheckCircle className="h-3 w-3" /> Verify
                            </button>
                            <button onClick={() => handleKYC(u.id, 'rejected')} data-testid={`reject-kyc-${u.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 border border-red-200 transition-colors">
                              <XCircle className="h-3 w-3" /> Reject
                            </button>
                          </>
                        )}
                        <button onClick={() => handleBlock(u.id)} data-testid={`block-user-${u.id}`} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                          u.status === 'blocked' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' 
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}>
                          {u.status === 'blocked' ? <><Shield className="h-3 w-3" /> Unblock</> : <><ShieldOff className="h-3 w-3" /> Block</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-16">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-[#6B7280] font-medium">No users found</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Try adjusting your search or filter</p>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
